import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

// Update this when the site is deployed.
const SITE_URL = "http://rcmnrm.in";
const SITE_NAME = "RM.NM Architects";
const SITE_TWITTER = "@rm_nm_architects";

const DEFAULT_DESCRIPTION =
  "RM.NM Architects — an architectural practice based at Bhavnagar, Gujarat. Established 1976 by Rajanikant Machhar, led today by Nishant Machhar. Residential, institutional and cultural architecture.";

const DEFAULT_KEYWORDS =
  "RM NM Architects, Rajanikant Machhar, Nishant Machhar, Bhavnagar architect, Gujarat architecture firm, best architecture Bhavnagar, residential architecture Gujarat, courtyard house, contemporary Indian architecture, architect Saurashtra";

export default function Seo({
  title,
  description,
  keywords,
  image,
  type = "website",
  noIndex = false,
  jsonLd,
}) {
  const { pathname } = useLocation();
  const canonical = `${SITE_URL}${pathname}`;

  const fullTitle = title
    ? `${title} — ${SITE_NAME}`
    : `${SITE_NAME} — Bhavnagar, Gujarat`;
  const desc = description || DEFAULT_DESCRIPTION;
  const kw = keywords || DEFAULT_KEYWORDS;
  const absImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE_URL}${image}`
    : `${SITE_URL}/images/chandroday/full/7.jpg`;

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <meta name="author" content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
      <meta name="googlebot" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:image" content={absImage} />
      <meta property="og:image:width" content="1800" />
      <meta property="og:image:height" content="1012" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE_TWITTER} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={absImage} />

      {/* Geo */}
      <meta name="geo.region" content="IN-GJ" />
      <meta name="geo.placename" content="Bhavnagar" />
      <meta name="geo.position" content="22.7880;72.1395" />
      <meta name="ICBM" content="22.7880, 72.1395" />

      {/* Structured data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

// Re-usable JSON-LD payload for the organization — pass to <Seo jsonLd={orgJsonLd}>.
export const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "additionalType": "https://schema.org/ArchitecturalService",
  name: SITE_NAME,
  alternateName: "Rajanikant Machhar & Nishant Machhar",
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.svg`,
  image: `${SITE_URL}/images/chandroday/full/7.jpg`,
  description: DEFAULT_DESCRIPTION,
  foundingDate: "1976",
  founder: {
    "@type": "Person",
    name: "Rajanikant Machhar",
    jobTitle: "Founder & Principal Architect",
  },
  employee: [
    {
      "@type": "Person",
      name: "Rajanikant Machhar",
      jobTitle: "Founder & Principal Architect",
    },
    {
      "@type": "Person",
      name: "Nishant Machhar",
      jobTitle: "Principal Architect",
    },
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "1939/A, Rupani Circle, Sardarnagar",
    addressLocality: "Bhavnagar",
    addressRegion: "Gujarat",
    postalCode: "364001",
    addressCountry: "IN",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 22.7880,
    longitude: 72.1395,
  },
  telephone: "+91 94262 09548",
  email: "contact@rcmnrm.co.in",
  sameAs: [
    "https://www.facebook.com/rm.nm.architects",
    "https://www.instagram.com/rm.nm.architects/",
  ],
  areaServed: { "@type": "Country", name: "India" },
  award: [
    "JIIA Award for Excellence in Architecture — 1994",
    "Architect of the Year — Commendation, 1996",
  ],
};

export const buildProjectJsonLd = (project, siteUrl = SITE_URL) => ({
  "@context": "https://schema.org",
  "@type": "CreativeWork",
  name: project.title,
  description: project.summary,
  creator: { "@type": "Organization", name: SITE_NAME, url: siteUrl },
  image: project.images.map((img) => `${siteUrl}${img.full}`),
  locationCreated: {
    "@type": "Place",
    name: project.location,
  },
  url: `${siteUrl}/works/${project.slug}`,
});
